import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';

// TS port of the source (Falah) app's pages/InventoryForm.jsx. Create-only —
// there is deliberately no `current_quantity` field: InventoryItemController
// ::validated() excludes it from both store() and update(), so a new item
// always starts at whatever the DB column defaults to (0) and can only be
// stocked up afterwards via the "Record a movement" flow on
// InventoryDetail.tsx.
interface InventoryFormState {
  name: string;
  category: string;
  unit: string;
  reorder_point: string;
  unit_price: string;
  facility_id: string | number;
  notes: string;
}

const emptyForm: InventoryFormState = {
  name: '',
  category: '',
  unit: 'pcs',
  reorder_point: '',
  unit_price: '',
  facility_id: '',
  notes: '',
};

interface FacilityOption {
  id: number;
  name: string;
}

type FormErrors = Record<string, string[]>;

export function InventoryForm() {
  const { lists, settings } = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState<InventoryFormState>(emptyForm);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ data: FacilityOption[] }>('/facilities', { params: { per_page: 100 } }).then((res) => setFacilities(res.data.data));
  }, []);

  const set =
    (key: keyof InventoryFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      const res = await api.post<{ id: number }>('/inventory-items', payload);
      navigate(`/resources/inventory/${res.data.id}`);
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Add Inventory</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
          <label className="span-2">
            Name *
            <input value={form.name} onChange={set('name')} required />
            {errors.name && <small className="error">{errors.name[0]}</small>}
          </label>
          <label>
            Category
            <select value={form.category} onChange={set('category')}>
              <option value="">—</option>
              {listOptions(lists, 'inventory_category').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Unit
            <select value={form.unit} onChange={set('unit')}>
              {listOptions(lists, 'inventory_unit').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reorder point
            <input type="number" step="0.01" min="0" value={form.reorder_point} onChange={set('reorder_point')} />
            <small className="muted">Flag as low stock at or below this quantity.</small>
          </label>
          <label>
            Unit price ({settings.currency})
            <input type="number" step="0.01" min="0" value={form.unit_price} onChange={set('unit_price')} />
            <small className="muted">Optional — used to suggest an amount when selling this item via a receipt.</small>
          </label>
          <label>
            Facility / storage location
            <select value={form.facility_id} onChange={set('facility_id')}>
              <option value="">—</option>
              {facilities.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} />
          </label>
          <p className="span-2 muted">
            New items start at 0 quantity — record a "Received" movement after saving to stock them up.
          </p>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/resources/inventory">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
